import { z } from 'zod';

/**
 * Empty string means the position is not ordered (`quantity = 0`, the row
 * simply omitted from the submitted lines) \u2014 `rotating-menu/design.md`
 * \u00a74: rows are fixed, one per position, so "no line for this position"
 * is expressed by a zero/blank quantity rather than the row's absence.
 */
export const positionQuantityFieldSchema = z
  .string()
  .trim()
  .refine((value) => value === '' || /^\d+$/.test(value), {
    message: 'Ingresá un número entero',
  });

/**
 * Shared by `OrderTemplateFormModal` and `ChangeRequestFormModal`
 * (`tasks.md` PR 6 REFACTOR criterion, extended to `rotating-menu`'s
 * position-based lines): both need the same posición+cantidad+notas
 * triple `orderLineFormSchema` (`order-form-schema.ts`) defines for
 * `OrderForm`, kept as its own copy here rather than imported from that
 * module \u2014 `OrderLineFormValues` is intentionally private to
 * `OrderFormValues`'s own shape. `positionName`/`dishName` are not part
 * of this schema: they are display-only metadata the editor renders
 * alongside each row from a parallel array, never submitted.
 */
export const menuLineFormSchema = z.object({
  positionId: z.string().uuid(),
  quantity: positionQuantityFieldSchema,
  notes: z.string().trim().max(1000),
});

export type MenuLineFormValues = z.infer<typeof menuLineFormSchema>;

export function defaultMenuLine(positionId: string): MenuLineFormValues {
  return { positionId, quantity: '', notes: '' };
}

/**
 * Rebuilds a position-keyed line array to exactly match `positionIds`
 * (order preserved), keeping the already-entered quantity/notes for a
 * position that stays in the list and dropping the rest — the sync
 * `OrderItemEditor`/`MenuLineEditor` run whenever the set of available
 * positions changes (new `serviceDate`, positions loaded). A position
 * missing from `currentItems` starts blank via `defaultMenuLine`.
 */
export function syncLinesToPositions<T extends MenuLineFormValues>(
  currentItems: T[],
  positionIds: string[],
  makeDefault: (positionId: string) => T,
): T[] {
  const byPositionId = new Map(currentItems.map((item) => [item.positionId, item]));

  return positionIds.map((positionId) => byPositionId.get(positionId) ?? makeDefault(positionId));
}
